/**
 * Massager dropdown for 統計一覧表 — refetches when yearmonth changes.
 *
 * Mirrors RA2 AJAX call to /rezepts/search_massagers on change of 提出年月.
 * Status NOT filtered (RA2 parity: getMassgers does not filter by status).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { fetchStatisticalMassagers } from '@/services/main/statisticalReport';
import type { StatisticalMassagerOption } from '@/pages/main/rezepts/statistical/types';

function useServiceType(): string {
  const theme = useTheme();
  return theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
    ? theme.serviceType
    : SERVICE_TYPE_A2;
}

export function useStatisticalMassagersQuery(yearmonth: string | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const enabled = !!tenant && !!clinicNumber && !!yearmonth && /^\d{6}$/.test(yearmonth);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['statistical', 'massagers', tenant, clinicNumber, serviceType, yearmonth],
    queryFn: () => fetchStatisticalMassagers(tenant, clinicNumber, yearmonth!, serviceType),
    enabled,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const massagers: StatisticalMassagerOption[] = data?.data?.data ?? [];

  return { massagers, isLoading, refetch };
}
